import React, { useState, useEffect } from 'react';
import styles from './HandleSelection.module.css';
import logo from '../../assets/insurtale-logo.webp';
import { validateKeycloakToken, selectHandleForKeycloak, saveKeycloakToken } from '../../services/authService';

// API base URL - use empty string for relative URLs in production, localhost for dev
const API_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_URL || '')
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001');

function HandleSelection({ onSuccess }) {
  const [handle, setHandle] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [keycloakId, setKeycloakId] = useState(null);
  const [keycloakToken, setKeycloakToken] = useState(null);
  const [suggestedHandle, setSuggestedHandle] = useState('');

  // CHECK for Keycloak token in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      authenticateWithKeycloak(token);
    }
  }, []);

  const authenticateWithKeycloak = async (token) => {
    setLoading(true);
    setError('');

    try {
      const result = await validateKeycloakToken(token);

      if (result.needsHandle) {
        // NEW USER - show handle selection
        setKeycloakId(result.keycloakId);
        setKeycloakToken(token);

        // Optionally pre-fill suggested handle
        if (result.suggestedHandle) {
          setSuggestedHandle(result.suggestedHandle);
          setHandle(result.suggestedHandle);
        }

        // Clean URL (remove token from address bar)
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        // EXISTING USER - auto-authenticate
        saveKeycloakToken(token);
        onSuccess(result);
      }
    } catch (err) {
      setError(err.message);
      // Show error and allow manual handle entry (fallback to dev mode)
      setKeycloakId(null);
      setKeycloakToken(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // CLIENT-SIDE validation
    if (handle.length < 3 || handle.length > 20) {
      setError('Handle must be 3-20 characters');
      return;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(handle)) {
      setError('Handle can only contain letters, numbers, _ and -');
      return;
    }

    setLoading(true);

    try {
      let result;

      if (keycloakId) {
        // KEYCLOAK MODE - select handle for Keycloak user
        result = await selectHandleForKeycloak(keycloakId, handle);
        saveKeycloakToken(keycloakToken);
      } else {
        // DEV MODE - regular handle-only login
        const response = await fetch(`${API_URL}/api/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ handle }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Login failed');
        }

        result = data;
      }

      // SUCCESS - call parent callback
      onSuccess(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <img src={logo} alt="insur_tale Logo" className={styles.logoImage} />
          <h1 className={styles.logo}>Arcology</h1>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <label className={styles.label}>
            {keycloakId ? 'Wähle deinen Arcology Handle:' : 'Wähle deinen Handle:'}
          </label>

          <input
            type="text"
            className={styles.input}
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="..."
            autoFocus
            disabled={loading}
            maxLength={20}
          />

          <div className={styles.rules}>
            <p>3-20 Zeichen</p>
            <p>Nur Buchstaben, Zahlen, _ und -</p>
            <p>Keine Sonder- oder Leerzeichen</p>
          </div>

          {error && (
            <div className={styles.error}>
              {error}
            </div>
          )}

          <button
            type="submit"
            className={styles.button}
            disabled={loading || !handle}
          >
            {loading ? 'Verbinden...' : 'Link to Arcology'}
          </button>
        </form>

        <p className={styles.hint}>
          Du kannst deinen Handle später mit <code>/nick</code> ändern
        </p>

        {keycloakId && (
          <p className={styles.ssoInfo}>
            🔐 Authenticated via insur_tale SSO
          </p>
        )}
      </div>
    </div>
  );
}

export default HandleSelection;
